import {
  type ArtifactEventPayload,
  DRAFT_ARTIFACT_CHAIN,
  type DraftArtifactKind,
  type StudyState,
  type StudyStepSlug,
} from '@sigora/domain';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type ArtifactStateRow,
  type ArtifactVersion,
  type AudienceCatalog,
  type Study,
  approveArtifact,
  fetchArtifact,
  fetchArtifactHistory,
  fetchArtifactState,
  rewordGuideQuestion,
  saveArtifact,
} from '../api/client.ts';
import { domainCodeOf } from '../api/errors.ts';
import { ArtifactCard } from './ArtifactCard.tsx';
import { checkoutSlotFor } from './EmbeddedCheckout.tsx';
import { LaunchPanel } from './LaunchPanel.tsx';
import {
  buildSidebar,
  approvalState,
  describeSave,
  readinessCameIntoView,
  readinessLabel,
  refusalMessage,
} from './state.ts';

export interface ArtifactSidebarProps {
  studyId: string;
  study: Study | null;
  /** Artifact parts the current conversation has streamed for this study. */
  streamed: readonly ArtifactEventPayload[];
  /**
   * The panel's demographics, so the audience card can name its filters. Owned by the shell rather
   * than read here: this component is keyed by study and remounts on every switch, and the catalog
   * is the same list for all of them.
   */
  audienceCatalog: AudienceCatalog | null;
  /** Bumped by the shell when a turn ends, so the stored state is re-read. */
  refreshToken: number;
  onStudyChanged: () => void;
  /**
   * An approval landed, and the server may have started the agent on what comes next. The turn id
   * is the shell's to watch — this component has no line to the conversation, by design — and null
   * means the approval stands but nothing new is being said.
   */
  onApproved?: (approved: { kind: DraftArtifactKind; turnId: string | null }) => void;
  /**
   * Which part of the study this renders, when it is standing in for one step rather than showing
   * the whole thing.
   *
   * A draft kind is that document and nothing else; `launch` is the readiness line and the price,
   * set-up or launched summary. Left out, it renders all of it, which is what a screen showing the
   * whole study wants.
   *
   * The reads behind it are not narrowed with it. The state read is one query for every artifact
   * the study has, and asking for a third of it per step would be three requests where there was
   * one, each of them able to disagree with the others.
   */
  only?: DraftArtifactKind | 'launch';
  /** Opens a step of this study: where the Launch panel's blockers link to. */
  onOpenStep?: (stepSlug: StudyStepSlug) => void;
}

/**
 * Whether the reader has asked for no animation. Read at the moment of the scroll rather than
 * subscribed to, because the only thing it decides is how one already-happening scroll behaves.
 */
const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type NoticeMap = Partial<Record<DraftArtifactKind, string>>;
type ContentMap = Partial<Record<DraftArtifactKind, unknown>>;
type VersionMap = Partial<Record<DraftArtifactKind, number>>;

/**
 * The artifact sidebar: what the study is made of, where each piece stands, and the two things
 * the user can do to it — approve it, or fix its wording.
 *
 * Ready is read, never asked for. Approving the last outstanding artifact promotes the study
 * inside the store's own transaction and an edit demotes it again, so this refetches the study
 * after a change and renders whatever it says. There is no call for the UI to make.
 */
export const ArtifactSidebar = ({
  studyId,
  study,
  streamed,
  audienceCatalog,
  refreshToken,
  onStudyChanged,
  onApproved,
  only,
  onOpenStep,
}: ArtifactSidebarProps) => {
  const [stored, setStored] = useState<ArtifactStateRow[]>([]);
  const [contents, setContents] = useState<ContentMap>({});
  /**
   * The version each loaded document came from. Kept beside the content rather than inferred
   * from the artifact's current version, because the two can differ: a streamed part can be
   * ahead of what was last read and carry no document of its own.
   */
  const [contentVersions, setContentVersions] = useState<VersionMap>({});
  const [history, setHistory] = useState<Partial<Record<DraftArtifactKind, ArtifactVersion[]>>>({});
  const [openHistory, setOpenHistory] = useState<DraftArtifactKind | null>(null);
  const [notices, setNotices] = useState<NoticeMap>({});
  const [busy, setBusy] = useState(false);
  /** The state read itself failed, which is not the same as a study with nothing in it. */
  const [loadFailed, setLoadFailed] = useState(false);
  /** Artifacts whose document could not be read, as distinct from ones not written yet. */
  const [unreadable, setUnreadable] = useState<ReadonlySet<DraftArtifactKind>>(new Set());

  /**
   * Which load is allowed to write. Responses come back in whatever order the network decides, so
   * without this the slowest one wins: switch studies and the first study's late reply lands as
   * the second study's state. That is not merely a wrong render — a full-document save built from
   * it carries the first study's content under the second study's id, and if the two happen to be
   * at the same version (they usually are, both at 1) the precondition sees nothing wrong and the
   * second study is overwritten with the first one's document.
   */
  const loadToken = useRef(0);

  /** The readiness block, so the view can be taken to it when there is suddenly something there. */
  const readiness = useRef<HTMLDivElement>(null);
  /**
   * The state this sidebar last rendered, and *which study that state was read off*. Held rather
   * than derived because the scroll is owed to a transition: without it the first render of an
   * already-ready study is indistinguishable from one that just became ready.
   *
   * The study is identified by the loaded study's own id rather than by the `studyId` prop. The two
   * disagree for one render on every switch — the prop changes as soon as the picker does, while
   * the study it names is still being fetched and the previous study is what this renders — and
   * pairing the new id with the old study's state is exactly the false `draft → ready` transition
   * that scrolled the page every time a ready study was opened.
   */
  const lastSeenState = useRef<{ studyId: string; state: StudyState } | null>(null);

  const reload = useCallback(async () => {
    const token = (loadToken.current += 1);

    const rows = await fetchArtifactState(studyId);

    /**
     * The rows each read is about, held beside the reads themselves.
     *
     * This list is what the results below are indexed against, and that is the whole point:
     * pairing a filtered result with `rows[index]` is correct only while every row is a chain row,
     * and a study holding its Materials or its Goal is not that. Once the two lengths differ the
     * pairing slides, and the chain kinds at the end of the list lose the version their document
     * was read at — which is the number a full-document save quotes as `expectedVersion`.
     */
    const chainRows = rows.filter((row) =>
      (DRAFT_ARTIFACT_CHAIN as readonly string[]).includes(row.kind),
    );

    // Settled, not all: one document failing to load is one card that cannot show its contents,
    // not a study with nothing in it. Rejecting the whole read would clear the rows and render
    // every artifact as "nothing written yet" — the one thing this sidebar must never say
    // untruthfully, since it is also what a user checks before concluding nothing has happened.
    const results = await Promise.allSettled(
      chainRows.map(async (row) => (await fetchArtifact(studyId, row.kind)).content),
    );

    if (token !== loadToken.current) {
      return;
    }

    const loaded: ContentMap = {};
    const versions: VersionMap = {};
    const failed = new Set<DraftArtifactKind>();
    results.forEach((result, index) => {
      const row = chainRows[index];
      if (row === undefined) {
        return;
      }

      const kind = row.kind as DraftArtifactKind;
      if (result.status === 'fulfilled') {
        loaded[kind] = result.value;
        versions[kind] = row.currentVersion;
      } else {
        failed.add(kind);
      }
    });

    // Applied together, after every read: setting the rows first would render a version number
    // from one load beside a document from the one before it.
    setStored(rows);
    setContents(loaded);
    setContentVersions(versions);
    setUnreadable(failed);
    setLoadFailed(false);
  }, [studyId]);

  useEffect(() => {
    const token = loadToken.current;

    void reload().catch(() => {
      // Only if nothing newer has started since; a superseded load's failure is not news. Reached
      // only when the state read itself failed — a per-document failure is carried in
      // `unreadable` instead, so it costs one card rather than the whole sidebar.
      if (loadToken.current === token + 1) {
        setStored([]);
        setContents({});
        setContentVersions({});
        setUnreadable(new Set());
        setLoadFailed(true);
      }
    });
  }, [reload, refreshToken]);

  useEffect(() => {
    const seen = lastSeenState.current;
    // A null study is a read that has not landed or has failed, which is not news about any study's
    // state: forgetting what was last seen would spend the scroll on the refresh that promotes a
    // study to ready if that same refresh happened to fail first.
    if (study !== null) {
      lastSeenState.current = { studyId: study.id, state: study.state };
    }

    if (
      study === null ||
      seen === null ||
      // A different study's last state says nothing about this one: opening a ready study is not
      // the same event as one becoming ready, and treating it as one scrolls on every switch.
      seen.studyId !== study.id ||
      !readinessCameIntoView(seen.state, study.state)
    ) {
      return;
    }

    // jsdom does not implement this one, and neither need a browser without a layout engine of its
    // own, so its absence costs the scroll rather than the render.
    const target = readiness.current;
    if (target === null || typeof target.scrollIntoView !== 'function') {
      return;
    }

    target.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, [study]);

  const entries = buildSidebar({ stored, streamed });
  /**
   * Payment closes the documents, not the freeze. `lockedAt` is written at step 6 of the
   * provisioning run, so between the charge and that step a study is paid for and its cards still
   * offer editing and approval — the server refuses neither, since the launch freeze keys on
   * `lockedAt` alone, and the run has already snapshotted the versions it is going to field. So an
   * edit accepted there is an edit to a document nobody will ever see, made against a study that
   * has been charged for. Reading `paidAt` as locked closes that window on the side that has it:
   * the server's own second refusal is the provisioning drift check, which fails the run rather
   * than the edit.
   */
  const locked = study?.lockedAt != null || study?.paidAt != null;
  const ready = study === null ? null : readinessLabel(study.state);

  /**
   * Every mutation funnels through here so that one rule holds everywhere: a refusal is shown as
   * itself, chosen by its code, and nothing is retried. The two refusals that matter most are
   * both conflicts — the document moved, or the change was structural — and retrying either
   * would either overwrite someone's work or ask for the same thing again.
   */
  const run = async (kind: DraftArtifactKind, action: () => Promise<string | null>) => {
    setBusy(true);
    setNotices((previous) => ({ ...previous, [kind]: undefined }));

    let outcome: string | null;
    try {
      outcome = await action();
    } catch (error: unknown) {
      setNotices((previous) => ({
        ...previous,
        [kind]: refusalMessage(
          domainCodeOf(error),
          error instanceof Error ? error.message : 'That did not go through. Try again.',
        ),
      }));
      setBusy(false);

      return;
    }

    if (outcome !== null) {
      setNotices((previous) => ({ ...previous, [kind]: outcome }));
    }

    /**
     * The refresh is a separate failure from the write, and reads as one. Sharing a `try` with the
     * action would report a save that has already landed — a new version, permanently, in an
     * append-only table — as a refusal telling the user their text was not saved, which would
     * reasonably lead them to write it again.
     */
    try {
      await reload();
      onStudyChanged();
    } catch {
      setNotices((previous) => ({
        ...previous,
        [kind]: `${previous[kind] ?? 'Saved.'} The view could not be refreshed — reload to see the current state.`,
      }));
    } finally {
      setBusy(false);
    }
  };

  /** What this render is for, as two questions rather than a comparison at each use. */
  const showsLaunch = only === undefined || only === 'launch';
  const shown = entries.filter(({ kind }) => only === undefined || only === kind);

  return (
    <aside className="sidebar" data-test-id="artifact-sidebar">
      {only === undefined && <h2>Your study</h2>}

      {loadFailed && (
        <p className="chat-error" data-test-id="sidebar-load-failed" role="alert">
          This study could not be read just now. Reload to try again — nothing here has changed.
        </p>
      )}

      {showsLaunch && (
        <div ref={readiness} data-test-id="sidebar-readiness">
          {ready !== null && (
            <p className="sidebar-ready" data-test-id="study-ready">
              {ready}
            </p>
          )}

          <LaunchPanel
            studyId={studyId}
            study={study}
            refreshToken={refreshToken}
            onStudyChanged={onStudyChanged}
            onOpenStep={onOpenStep}
            checkout={checkoutSlotFor(studyId)}
          />
        </div>
      )}

      {!loadFailed &&
        shown.map(({ kind, artifact }) => (
          <ArtifactCard
            key={kind}
            kind={kind}
            artifact={artifact}
            approval={approvalState({ entries, kind, locked })}
            // The document and the version it came from, chosen together. A streamed part ahead of
            // the stored row may carry no document — its content is optional — and pairing that
            // newer version number with the older stored document would let a save quote a version
            // it never rendered, pass the precondition, and revert the newer one.
            content={artifact?.content ?? contents[kind]}
            contentVersion={
              artifact?.content === undefined ? contentVersions[kind] : artifact.version
            }
            contentUnreadable={unreadable.has(kind)}
            audienceCatalog={audienceCatalog}
            history={history[kind]}
            historyOpen={openHistory === kind}
            notice={notices[kind] ?? null}
            busy={busy}
            locked={locked}
            onApprove={() => {
              if (artifact?.artifactId == null) {
                return;
              }

              void run(kind, async () => {
                const { turnId } = await approveArtifact({
                  studyId,
                  artifactId: artifact.artifactId as string,
                  version: artifact.version,
                });
                onApproved?.({ kind, turnId });

                return null;
              });
            }}
            onToggleHistory={() => {
              if (openHistory === kind) {
                setOpenHistory(null);
                return;
              }

              setOpenHistory(kind);
              if (artifact?.artifactId != null) {
                void fetchArtifactHistory(studyId, artifact.artifactId).then(
                  (versions) => {
                    setHistory((previous) => ({ ...previous, [kind]: versions }));
                  },
                  () => {
                    // Left unhandled, this sat on "Loading…" for as long as the panel stayed open.
                    setNotices((previous) => ({
                      ...previous,
                      [kind]: 'The version history could not be loaded.',
                    }));
                    setOpenHistory(null);
                  },
                );
              }
            }}
            onSaveDocument={(content, baseVersion) => {
              void run(
                kind,
                async () =>
                  describeSave(
                    // The version the editor was opened on, reported back by the body — not the
                    // version showing now. A turn can land between the two, and quoting the newer
                    // one would assert authorship of a document the user never saw, which is the
                    // overwrite the precondition exists to refuse.
                    await saveArtifact({ studyId, kind, content, expectedVersion: baseVersion }),
                  ).message,
              );
            }}
            onRewordQuestion={(questionId, text, baseVersion) => {
              void run(kind, async () => {
                const written = await rewordGuideQuestion({
                  studyId,
                  questionId,
                  text,
                  expectedVersion: baseVersion,
                  acknowledgedDownstream: null,
                });

                return written.deduped
                  ? 'No changes to save.'
                  : `Saved as version ${String(written.version)}.`;
              });
            }}
          />
        ))}
    </aside>
  );
};
